import { useMobileMenuActions, useMobileMenuState } from "@/store/mobileMenuSlice";
import { DashboardIcon, UploadsIcon, UploadingIcon, ClipsIcon, ContributorsIcon, CollapseIcon, ExclamationCircleIcon, LockIcon } from "@/icons"
import { Badge, Box } from "@mui/material";
import {
    SlidersHorizontal,
    RadioTower,
    GraduationCap,
    Megaphone,
} from "lucide-react";

import {
  NavPlaceholder,
  Nav,
  NavIcon,
  NavLink,
  NavDivider,
  NavContainer,
  NavHeader,
  NavLabel,
  CollapseButton,
  MobileCloseButton,
} from "./Navbar.elements";
import { useActiveUploads, useAuthState } from "@/store";
import { Logo } from "../Logo/Logo";
import { useIsMobile } from '../hooks';

// Marks an admin-only nav entry with a small gold lock badge in the bottom-right corner.
// Reusing the gold (#f5c754) that NavLink already uses for hover/active so admin items
// feel native to the existing palette rather than a new accent.
const AdminBadgedIcon = ({ icon: MainIcon }) => (
  <Box sx={{ position: "relative", width: 22, height: 22, display: "inline-flex" }}>
    <Box component={MainIcon} sx={{ width: "100%", height: "100%" }} />
    <Box
      sx={{
        position: "absolute",
        right: -3,
        bottom: -3,
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: "#10121E",
        border: "1.5px solid #f5c754",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Box
        component={LockIcon}
        sx={{ width: 7, height: 7, color: "#f5c754" }}
      />
    </Box>
  </Box>
);

export const Navbar = () => {
  const isMobile = useIsMobile();
  const { currentUser } = useAuthState();
  const { role } = currentUser || {};
  const isAdmin = role === "admin";
  const isManager = role === "manager";
  const { isMobileMenuOpen, isSidebarCollapsed } = useMobileMenuState();
  const { toggleMobileMenu, toggleSidebar } = useMobileMenuActions();
  const collapsed = !isMobile && isSidebarCollapsed;
  const activeUploadsCount = useActiveUploads().length;

  if (!currentUser) {
    console.log("current user is: ", currentUser);
    return null;
  } else {
    return (
      <NavPlaceholder isCollapsed={collapsed}>
        <NavContainer isMobileMenuOpen={isMobileMenuOpen}>
          <NavHeader isCollapsed={collapsed}>
            <Logo />
            {isMobile ? (
                <MobileCloseButton onClick={toggleMobileMenu} />
            ) : (
                <CollapseButton isCollapsed={collapsed} onClick={toggleSidebar}>
                  <CollapseIcon />
                </CollapseButton>
            )}
          </NavHeader>
          <Nav>
            <NavLink to="/" onClick={toggleMobileMenu} isCollapsed={collapsed}>
              <NavIcon as={DashboardIcon} />
              <NavLabel isCollapsed={collapsed}>Dashboard</NavLabel>
            </NavLink>

            <NavLink
              to="/uploads"
              end
              onClick={toggleMobileMenu}
              isCollapsed={collapsed}
              data-testid="nav-uploads"
            >
              <NavIcon as={UploadsIcon} />
              <NavLabel isCollapsed={collapsed}>Uploads</NavLabel>
            </NavLink>
            
            <NavLink to="/clips" onClick={toggleMobileMenu} isCollapsed={collapsed}>
              <NavIcon as={ClipsIcon} />
              <NavLabel isCollapsed={collapsed}>Clips</NavLabel>
            </NavLink>

            <NavLink
              to="/uploads/in-progress"
              onClick={toggleMobileMenu}
              isCollapsed={collapsed}
              data-testid="nav-uploading"
            >
              <Badge
                  badgeContent={activeUploadsCount}
                  color="secondary"
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      transform: "scale(0.8) translate(55%, -55%)",
                    },
                  }}
              >
                <NavIcon as={UploadingIcon} />
              </Badge>
              <NavLabel isCollapsed={collapsed}>Uploading</NavLabel>
            </NavLink>

            <NavLink
              to="/tutorials"
              onClick={toggleMobileMenu}
              isCollapsed={collapsed}
              data-testid="nav-tutorials"
              data-tour="nav-tutorials"
            >
              <NavIcon><GraduationCap size={22} /></NavIcon>
              <NavLabel isCollapsed={collapsed}>Tutorials</NavLabel>
            </NavLink>
            
            {(isAdmin || isManager) && (
              <>
                <NavDivider/>
                <NavLink to="/manageUsers" onClick={toggleMobileMenu} isCollapsed={collapsed}>
                  <NavIcon as={ContributorsIcon} />
                  <NavLabel isCollapsed={collapsed}>Contributors</NavLabel>
                </NavLink>
                {isAdmin && (
                    <NavLink to="/admin/alerts" onClick={toggleMobileMenu} isCollapsed={collapsed}>
                      <NavIcon>
                        <AdminBadgedIcon icon={ExclamationCircleIcon} />
                      </NavIcon>
                      <NavLabel isCollapsed={collapsed}>System Alert</NavLabel>
                    </NavLink>
                )}
                {isAdmin && (
                    <NavLink to="/admin/settings" onClick={toggleMobileMenu} isCollapsed={collapsed}>
                      <NavIcon>
                        <AdminBadgedIcon icon={SlidersHorizontal} />
                      </NavIcon>
                      <NavLabel isCollapsed={collapsed}>Settings</NavLabel>
                    </NavLink>
                )}
                {isAdmin && (
                    <NavLink to="/admin/uploads" onClick={toggleMobileMenu} isCollapsed={collapsed}>
                      <NavIcon>
                        <AdminBadgedIcon icon={RadioTower} />
                      </NavIcon>
                      <NavLabel isCollapsed={collapsed}>Live Uploads</NavLabel>
                    </NavLink>
                )}
                {isAdmin && (
                    <NavLink to="/admin/emails" onClick={toggleMobileMenu} isCollapsed={collapsed}>
                      <NavIcon>
                        <AdminBadgedIcon icon={Megaphone} />
                      </NavIcon>
                      <NavLabel isCollapsed={collapsed}>What's New Email</NavLabel>
                    </NavLink>
                )}
              </>
            )}
          </Nav>
        </NavContainer>
      </NavPlaceholder>
    );
  }
};
